import { useRef, useState } from 'react';
import { isValidDate, CUSTOM_RANGE_KEY } from '@/utilities/payPeriod';

// The picker's dropdown uses the same sentinel as a custom `filter.week`'s key.
export const CUSTOM_RANGE_VALUE = CUSTOM_RANGE_KEY;

/**
 * State, validation, and handlers for the shared PayPeriodPicker. Keeps the picker
 * component display-only: the custom-range drafts, popover open/close, ordering
 * validation, and apply/clear logic all live here rather than in the JSX.
 *
 * Drafts are Date objects for the MUI DatePicker (rendered MM/DD/YYYY regardless of locale).
 */
export const usePayPeriodPicker = ({ state, callbacks }) => {
    const anchorRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [draftStart, setDraftStart] = useState(null);
    const [draftEnd, setDraftEnd] = useState(null);

    const selected = state?.filter?.week;
    // Selected value resolves to either a preset week's `key` or the custom-range sentinel.
    const selectedValue = selected?.isCustom ? CUSTOM_RANGE_VALUE : selected?.key || '';

    // Apply only when both dates are valid and ordered (start on or before end).
    const canApply = isValidDate(draftStart) && isValidDate(draftEnd) && draftStart <= draftEnd;
    // Flag an explicit out-of-order range so the End field can show an error state.
    const endBeforeStart = isValidDate(draftStart) && isValidDate(draftEnd) && draftStart > draftEnd;

    const handleChange = (e) => {
        const value = e.target.value;
        if (value === CUSTOM_RANGE_VALUE) {
            // Seed the popover with the current selection if it's already a custom range.
            if (selected?.isCustom) {
                setDraftStart(selected.sow);
                setDraftEnd(selected.eow);
            } else {
                setDraftStart(null);
                setDraftEnd(null);
            }
            setPopoverOpen(true);
            return;
        }
        const obj = state?.weeks.find(({ key }) => key === value);
        callbacks.setFilter((prev) => ({ ...prev, week: obj }));
    };

    const clearSelection = () => callbacks.setFilter((prev) => ({ ...prev, week: undefined }));

    const applyCustomRange = () => {
        if (!canApply) return;
        callbacks.setCustomRange(draftStart, draftEnd);
        setPopoverOpen(false);
    };

    const closePopover = () => setPopoverOpen(false);

    return {
        anchorRef,
        popoverOpen,
        draftStart,
        draftEnd,
        setDraftStart,
        setDraftEnd,
        selected,
        selectedValue,
        canApply,
        endBeforeStart,
        handleChange,
        clearSelection,
        applyCustomRange,
        closePopover,
    };
};
